import { put, select } from "redux-saga/effects";
import {
  ConnectionResultEnum,
  InvalidEnum,
  PaymentSessionToken,
  TravelWalletOffer,
} from "redmond";
import { getHSPEarnOfferDescription, isEarnOfferV2, isSpendAndGetOffer } from "halifax";
import { Payment } from "@b2bportal/purchase-api";
import { getIsTravelApp, getUserDeviceData } from "@capone/common";
import { actions } from "../../actions";
import { schedulePayment } from "../../../../api/v1/book/book-flow/schedulePayment";
import {
  getOffers,
  getOpaquePayments,
  getPaymentRequestV2,
  getSession,
  getUseV1PurchaseFlow,
  getTripDescription,
} from "../../reducer";
import { IStoreState } from "../../../../reducers/types";
import { schedulePaymentV1 } from "../../../../api/v2/book/book-flow/schedulePayment";
import { authorizeEarnOffer } from "../../../../api/v1/travel-wallet/authorizeEarnOffer";
import { ISetScheduleBook } from "../../actions/actions";

export function* scheduleBookSaga({ initialTime, agentFee }: ISetScheduleBook) {
  try {
    const state: IStoreState = yield select();
    const session = getSession(state);
    const version = getUseV1PurchaseFlow(state) ? "v1" : "v0";

    if (!session) {
      throw Error("Session is undefined");
    }

    if (version === "v0") {
      yield schedulePayment(getPaymentRequestV2(state)!);
      yield put(actions.pollConfirmationDetails(session!));
    } else {
      const corporateOutOfPolicyReason = state.carBook.approvalRequestReason;
      const corporateTripDescription = getTripDescription(state);
      const payments = getOpaquePayments(state);
      if (!payments) {
        throw new Error("Invalid Request.");
      }

      const offers: TravelWalletOffer[] | undefined = yield select(getOffers);
      const appliedOfferPayment = payments?.find(
        (payment) => payment.type === Payment.TravelWalletOffer
      );

      const appliedOffer = offers?.find(
        (offer) => offer.id === appliedOfferPayment?.value.offerId
      );

      const hspEarnDescription = getHSPEarnOfferDescription(appliedOffer);
      const isV2EarnOffer = isEarnOfferV2(appliedOffer);
      const isSpendAndGet =
        appliedOffer && isSpendAndGetOffer(appliedOffer.descriptions);

      // authorize hsp earn offer before fulfillment (not for spend-and-get offers or v2 earn offers)
      if (hspEarnDescription && !isV2EarnOffer && appliedOffer?.id) {
        yield authorizeEarnOffer({
          earnOfferId: appliedOffer.id,
        });
      }

      const { isTravelApp } = getIsTravelApp();

      const userDeviceData = getUserDeviceData();

      const channel = (() => {
        switch (true) {
          case isTravelApp:
            return "TravelApp";
          case userDeviceData.deviceType === "mobile":
            return "Mobile";
          default:
            return "Web";
        }
      })();

      // Don't include legacy HSP earn offers or spend-and-get offers in payments.
      // v2 earn offers are sent as regular payments.
      const shouldFilterOffer =
        (hspEarnDescription && !isV2EarnOffer) || isSpendAndGet;

      const result: string = yield schedulePaymentV1({
        token: session,
        payments: shouldFilterOffer
          ? payments.filter(
              (payment) => payment.type !== Payment.TravelWalletOffer
            )
          : payments,
        corporateOutOfPolicyReason,
        corporateTripDescription,
        channel,
      });
      yield put(
        actions.setv1FulfillSession({ value: result } as PaymentSessionToken)
      );

      yield put(
        actions.pollConfirmationDetails(
          {
            value: result,
          } as PaymentSessionToken,
          agentFee,
          initialTime
        )
      );
    }

    yield put(actions.setScheduleBookSuccess());
  } catch (e) {
    yield put(
      actions.setScheduleBookFailed({
        Invalid: InvalidEnum.Missing,
        ConnectionResult: ConnectionResultEnum.Invalid,
      })
    );
  }
}
